import { type ReactNode } from 'react';

import PageShell from '@/components/PageShell';
import PageCTA from '@/components/PageCTA';
import MiaOrb from '@/components/MiaOrb';
import {
  Copyright as LandingCopyright,
  PartnerBar,
} from '@/components/LandingContext';

type Mood = 'idle' | 'asking' | 'thinking' | 'success';

type PaymentScreenProps = {
  heading: string;
  body: ReactNode;
  /** Gift summary chip for the left pane; omitted when there is no gift to show. */
  summary?: ReactNode;
  /** Mood of the default orb. Ignored when `icon` is given. */
  mood?: Mood;
  /** Replaces the orb — the outcome screens use their own status mark. */
  icon?: ReactNode;
  /** Announces the copy to assistive tech while something is in progress. */
  busy?: boolean;
  /** Status strip above the copy — the outcome screens' design has one. */
  banner?: ReactNode;
  /** Sits between the mark and the actions (the "why this happens" card). */
  children?: ReactNode;
  action?: ReactNode;
  className?: string;
  /** Passed through to the shell; see GIFTWEB-32 AC 2. */
  showHelp?: boolean;
};

/**
 * The two-pane V2 frame shared by every screen of the payment step — handoff,
 * processing and outcome (GIFTWEB-26 AC 11).
 *
 * Left pane carries the context (gift summary, trust footer), right pane the
 * copy, the mark and whatever the user can do. Mirrors the message step so the
 * journey does not change shape at the last moment.
 */
function PaymentScreen({
  heading,
  body,
  summary,
  mood = 'idle',
  icon,
  busy = false,
  banner,
  children,
  action,
  className,
  showHelp,
}: PaymentScreenProps) {
  return (
    <PageShell
      variant='split'
      className={className}
      showHelp={showHelp}
      left={
        <>
          {summary}

          <div className='hidden lg:block'>
            <PartnerBar variant='desktop' />
            <LandingCopyright marginTop={12} />
          </div>
        </>
      }
      right={
        <>
          {/* Stretches on mobile to centre the group in what is left of the
              screen; on desktop the pane already centres its own content, so
              stretching here would only strand the CTA at the floor. */}
          <div className='flex flex-1 flex-col items-center justify-center gap-7 text-center lg:flex-none lg:pb-4'>
            {banner}

            {/* `role` sits here, not on the heading — announcing the busy
                state must not cost the h1 its heading semantics. */}
            <div className='max-w-[24rem]' {...(busy ? { role: 'status' } : {})}>
              <h1 className='text-[27px] leading-tight font-bold tracking-[-0.02em] text-[#1a201d] lg:text-[31px] lg:tracking-[-0.025em]'>
                {heading}
              </h1>
              <p className='mt-2.5 text-[14px] leading-[1.55] text-[#5a635e] lg:mt-[11px] lg:text-[14.5px]'>
                {body}
              </p>
            </div>

            {icon ?? <MiaOrb size={96} mood={mood} />}

            {children}
          </div>

          <PageCTA>
            {action}
            <div className='lg:hidden'>
              <PartnerBar variant='mobile' />
              <LandingCopyright marginTop={0} />
            </div>
          </PageCTA>
        </>
      }
    />
  );
}

export default PaymentScreen;
